import { useState } from 'react';
import { Link } from 'react-router-dom';
import useSWR from 'swr';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Loader2, Mail, CheckCircle } from 'lucide-react';

interface User {
  id: string;
  email: string;
  emailVerified: boolean;
}

export default function VerifyEmail() {
  const { data: user } = useSWR<User>('/api/user');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [email, setEmail] = useState('');

  const handleResend = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setPending(true);
    setError('');

    const emailToUse = user?.email || email;

    if (!emailToUse) {
      setError('Please enter your email address');
      setPending(false);
      return;
    }

    try {
      const response = await fetch('/api/auth/resend-verification', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ email: emailToUse }),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Failed to send verification email');
        setPending(false);
        return;
      }

      setSuccess(true);
      setPending(false);
    } catch (err) {
      setError('An error occurred. Please try again.');
      setPending(false);
    }
  };

  if (success) {
    return (
      <div className="h-screen flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-8 bg-background">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="flex justify-center mb-6">
            <div className="rounded-full bg-success-surface-strong p-3">
              <CheckCircle className="h-8 w-8 text-success-foreground" />
            </div>
          </div>
          <h2 className="text-center text-3xl font-medium text-content-heading">
            Email sent
          </h2>
          <p className="mt-4 text-center text-content-secondary">
            We've sent a new verification link to{' '}
            <span className="font-medium">{user?.email || email}</span>.
            The link will expire in 24 hours.
          </p>
        </div>

        <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-xs">
          <Button
            variant="outline"
            className="w-full"
            onClick={() => setSuccess(false)}
          >
            Send another email
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-8 bg-background">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center mb-6">
          <div className="rounded-full bg-info-surface-strong p-3">
            <Mail className="h-8 w-8 text-info-foreground" />
          </div>
        </div>
        <h2 className="text-center text-3xl font-medium text-content-heading">
          Verify your email
        </h2>
        <p className="mt-4 text-center text-content-secondary">
          {user ? (
            <>
              We've sent a verification link to{' '}
              <span className="font-medium">{user.email}</span>.
              Click the link in the email to verify your account.
            </>
          ) : (
            'Please verify your email address to continue.'
          )}
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-xs">
        <form className="space-y-6" onSubmit={handleResend}>
          {!user && (
            <Input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              maxLength={255}
              inlineLabel="Email"
              placeholder="Enter your email"
              className="w-full"
            />
          )}

          {error && (
            <div className="text-danger-text text-sm">{error}</div>
          )}

          <Button
            type="submit"
            disabled={pending}
            className="w-full"
            variant="outline"
          >
            {pending ? (
              <>
                <Loader2 className="animate-spin mr-2 h-4 w-4" />
                Sending...
              </>
            ) : (
              'Resend verification email'
            )}
          </Button>
        </form>

        <div className="mt-6 text-center">
          <Link
            to="/sign-in"
            className="flex items-center justify-center gap-2 text-sm font-medium text-primary hover:text-primary/80"
          >
            Back to sign in
          </Link>
        </div>
      </div>
    </div>
  );
}
